import React, { useState } from "react";
import { UserProfile } from "../auth/session";
import { ChessBoardViewer } from "../components/ChessBoardViewer";
import { StatCard } from "../components/StatCard";

interface StudentDashboardProps {
  user: UserProfile;
}

type Tab = "overview" | "games" | "coach" | "learning" | "academy" | "account";

interface GameRecord {
  id: string;
  opponent: string;
  opponentRating: number;
  result: "1-0" | "0-1" | "1/2-1/2";
  timeControl: string;
  date: string;
  fen: string;
  moves: readonly string[];
  accuracy: number;
}

const SAMPLE_GAMES: GameRecord[] = [
  {
    id: "game-01",
    opponent: "Vikram R. (Coimbatore)",
    opponentRating: 1520,
    result: "1-0",
    timeControl: "10+0 Rapid",
    date: "15 Sep 2026",
    fen: "r1bqkb1r/pp2pppp/2np1n2/8/3NP3/2N5/PPP2PPP/R1BQKB1R w KQkq - 1 6",
    moves: ["1. e4 c5", "2. Nf3 d6", "3. d4 cxd4", "4. Nxd4 Nf6", "5. Nc3 Nc6"],
    accuracy: 88,
  },
  {
    id: "game-02",
    opponent: "Kavitha M. (Madurai)",
    opponentRating: 1565,
    result: "1/2-1/2",
    timeControl: "15+10 Rapid",
    date: "14 Sep 2026",
    fen: "r1bqk2r/pppp1ppp/2n2n2/2b1p3/2B1P3/2N2N2/PPPP1PPP/R1BQK2R w KQkq - 4 5",
    moves: ["1. e4 e5", "2. Nf3 Nc6", "3. Bc4 Bc5", "4. Nc3 Nf6"],
    accuracy: 91,
  },
  {
    id: "game-03",
    opponent: "Siddharth S. (Chennai)",
    opponentRating: 1580,
    result: "0-1",
    timeControl: "5+3 Blitz",
    date: "12 Sep 2026",
    fen: "rnbqkb1r/pppp1ppp/5n2/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 2 3",
    moves: ["1. e4 e5", "2. Nf3 Nf6"],
    accuracy: 74,
  },
];

export function StudentDashboard({
  user,
}: StudentDashboardProps): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("overview");
  const [selectedGame, setSelectedGame] = useState<GameRecord>(
    SAMPLE_GAMES[0]!,
  );
  const [coachInput, setCoachInput] = useState("");
  const [coachConversation, setCoachConversation] = useState([
    {
      sender: "coach",
      text: "Vanakkam Arjun! In your recent game against Vikram, your knight maneuver in the Sicilian (d4 to c6 exchange) was very sharp. Today let's practice Rook endgames to solidify your advantage.",
    },
  ]);
  const [coachBusy, setCoachBusy] = useState(false);

  function handleSendCoachQuestion(e: React.FormEvent) {
    e.preventDefault();
    if (!coachInput.trim()) return;

    const query = coachInput.trim();
    setCoachInput("");
    setCoachConversation((prev) => [
      ...prev,
      { sender: "student", text: query },
    ]);
    setCoachBusy(true);

    setTimeout(() => {
      setCoachConversation((prev) => [
        ...prev,
        {
          sender: "coach",
          text: `Great question regarding "${query}". Remember to calculate candidate moves systematically: look for checks, captures, and threats (CCT) before committing pawn pushes.`,
        },
      ]);
      setCoachBusy(false);
    }, 600);
  }

  return (
    <div className="role-dashboard student-dashboard">
      <nav className="dashboard-subnav" aria-label="Student sections">
        <button
          type="button"
          className={`subnav-link ${tab === "overview" ? "active" : ""}`}
          onClick={() => setTab("overview")}
        >
          Overview
        </button>
        <button
          type="button"
          className={`subnav-link ${tab === "games" ? "active" : ""}`}
          onClick={() => setTab("games")}
        >
          Games & Review
        </button>
        <button
          type="button"
          className={`subnav-link ${tab === "coach" ? "active" : ""}`}
          onClick={() => setTab("coach")}
        >
          My Zhatura Coach
        </button>
        <button
          type="button"
          className={`subnav-link ${tab === "learning" ? "active" : ""}`}
          onClick={() => setTab("learning")}
        >
          Learning & Drills
        </button>
        <button
          type="button"
          className={`subnav-link ${tab === "academy" ? "active" : ""}`}
          onClick={() => setTab("academy")}
        >
          My Academy
        </button>
        <button
          type="button"
          className={`subnav-link ${tab === "account" ? "active" : ""}`}
          onClick={() => setTab("account")}
        >
          Account
        </button>
      </nav>

      <div className="dashboard-content">
        {tab === "overview" && (
          <div className="overview-tab">
            <div className="welcome-banner">
              <h2>Vanakkam, {user.displayName}!</h2>
              <p>
                Ready to sharpen your tactics today? Your current rating is on
                track for the State U-16 Trials.
              </p>
            </div>

            <div className="stat-grid">
              <StatCard
                label="Rapid Rating"
                value={user.rating || 1540}
                subtext="Tamil Nadu State Ranking #412"
                trend={{ direction: "up", value: "+28 this month" }}
                icon="♟"
              />
              <StatCard
                label="Games Played"
                value="44"
                subtext="24 Wins • 8 Draws • 12 Losses"
                icon="⚔"
              />
              <StatCard
                label="Tactical Accuracy"
                value="84.2%"
                subtext="Stockfish 18 Evaluation average"
                trend={{ direction: "up", value: "+3.1%" }}
                icon="🎯"
              />
              <StatCard
                label="Tactics Solved"
                value="182"
                subtext="Curriculum progress: 68%"
                icon="🧩"
              />
            </div>

            <div className="section-card">
              <div className="section-card-header">
                <h3>Active Training Goal</h3>
                <span className="badge badge-accent">In Progress</span>
              </div>
              <p className="goal-title">
                Mastering Knight Outposts in the Sicilian Defense
              </p>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: "70%" }}
                ></div>
              </div>
              <p className="goal-subtext">
                Assigned by Coach Ramesh S. • Due in 3 days
              </p>
            </div>
          </div>
        )}

        {tab === "games" && (
          <div className="games-tab">
            <div className="games-layout">
              <div className="games-list-panel">
                <h3>Recent Rated Games</h3>
                <div className="games-list">
                  {SAMPLE_GAMES.map((game) => (
                    <button
                      key={game.id}
                      type="button"
                      className={`game-item-card ${selectedGame.id === game.id ? "active" : ""}`}
                      onClick={() => setSelectedGame(game)}
                    >
                      <div className="game-item-top">
                        <span className="game-opponent">{game.opponent}</span>
                        <span
                          className={`game-result result-${game.result === "1-0" ? "win" : game.result === "0-1" ? "loss" : "draw"}`}
                        >
                          {game.result}
                        </span>
                      </div>
                      <div className="game-item-bottom">
                        <span>{game.timeControl}</span>
                        <span>Accuracy: {game.accuracy}%</span>
                        <span>{game.date}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="game-board-panel">
                <h3>Game Analysis: vs {selectedGame.opponent}</h3>
                <p className="game-meta-text">
                  Result: <strong>{selectedGame.result}</strong> • Time Control:{" "}
                  {selectedGame.timeControl} • Date: {selectedGame.date}
                </p>

                <ChessBoardViewer
                  fen={selectedGame.fen}
                  moves={selectedGame.moves}
                  orientation="white"
                />

                <div className="moves-notation-box">
                  <h4>Recorded Moves</h4>
                  <p className="notation-text">
                    {selectedGame.moves.join("  ")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "coach" && (
          <div className="coach-tab">
            <div className="coach-chat-card">
              <div className="coach-chat-header">
                <div className="coach-avatar">♞</div>
                <div>
                  <h3>My Zhatura Coach</h3>
                  <p className="coach-status">Active • Powered by panankAI</p>
                </div>
              </div>

              <div
                className="coach-messages-area"
                role="log"
                aria-label="Coach conversation"
              >
                {coachConversation.map((msg, idx) => (
                  <div key={idx} className={`coach-msg msg-${msg.sender}`}>
                    <div className="msg-bubble">
                      <span className="msg-sender-name">
                        {msg.sender === "coach" ? "Coach" : "You"}
                      </span>
                      <p>{msg.text}</p>
                    </div>
                  </div>
                ))}
                {coachBusy && (
                  <div className="coach-msg msg-coach">
                    <div className="msg-bubble">
                      <em>Coach is analyzing your position…</em>
                    </div>
                  </div>
                )}
              </div>

              <form
                onSubmit={handleSendCoachQuestion}
                className="coach-input-form"
              >
                <input
                  type="text"
                  value={coachInput}
                  onChange={(e) => setCoachInput(e.target.value)}
                  placeholder="Ask your coach about a move, opening or endgame…"
                  disabled={coachBusy}
                />
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={coachBusy || !coachInput.trim()}
                >
                  Ask Coach
                </button>
              </form>
            </div>
          </div>
        )}

        {tab === "learning" && (
          <div className="learning-tab">
            <h3>Assigned Drills & Curriculum</h3>
            <div className="drills-grid">
              <div className="drill-card">
                <div className="drill-badge">Assignment</div>
                <h4>Tactics: Rook & Pawn Endgame Fortress</h4>
                <p>
                  12 interactive puzzles focusing on Lucena & Philidor
                  positions.
                </p>
                <div className="drill-footer">
                  <span className="drill-status status-pending">
                    8 of 12 completed
                  </span>
                  <button type="button" className="btn-secondary btn-sm">
                    Continue Drill
                  </button>
                </div>
              </div>

              <div className="drill-card">
                <div className="drill-badge">Opening Prep</div>
                <h4>Sicilian Defense: Najdorf Outposts</h4>
                <p>
                  Key d5 outpost square defense against White's English Attack.
                </p>
                <div className="drill-footer">
                  <span className="drill-status status-completed">
                    Completed 100%
                  </span>
                  <button type="button" className="btn-secondary btn-sm">
                    Review
                  </button>
                </div>
              </div>

              <div className="drill-card">
                <div className="drill-badge">Homework</div>
                <h4>FIDE 2026 Master Puzzle Set #4</h4>
                <p>Blunder detection drills from Chennai Open 2026 round 5.</p>
                <div className="drill-footer">
                  <span className="drill-status status-pending">
                    Due in 2 days
                  </span>
                  <button type="button" className="btn-secondary btn-sm">
                    Start
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "academy" && (
          <div className="academy-tab">
            <div className="academy-info-card">
              <div className="academy-header-block">
                <div>
                  <span className="eyebrow">Affiliated Academy</span>
                  <h3>Tamil Nadu Chess Academy</h3>
                  <p className="academy-branch">
                    Central Chennai Branch • District: Chennai
                  </p>
                </div>
                <span className="badge badge-success">Active Student</span>
              </div>

              <div className="academy-details-grid">
                <div className="academy-detail-item">
                  <span className="detail-label">Assigned Coach</span>
                  <span className="detail-val">
                    Coach Ramesh S. (FIDE Trainer)
                  </span>
                </div>
                <div className="academy-detail-item">
                  <span className="detail-label">Training Batch</span>
                  <span className="detail-val">
                    Intermediate Batch A (Saturday / Sunday)
                  </span>
                </div>
                <div className="academy-detail-item">
                  <span className="detail-label">Next Live Session</span>
                  <span className="detail-val">Saturday 10:00 AM IST</span>
                </div>
                <div className="academy-detail-item">
                  <span className="detail-label">Attendance Record</span>
                  <span className="detail-val">96% (24 of 25 sessions)</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "account" && (
          <div className="account-tab">
            <div className="section-card">
              <h3>Player Profile Details</h3>
              <div className="account-form-grid">
                <div>
                  <span className="field-label">Full Name</span>
                  <p className="field-val">{user.displayName}</p>
                </div>
                <div>
                  <span className="field-label">Email Address</span>
                  <p className="field-val">{user.email}</p>
                </div>
                <div>
                  <span className="field-label">Territory & District</span>
                  <p className="field-val">{user.territory}</p>
                </div>
                <div>
                  <span className="field-label">Player Account ID</span>
                  <p className="field-val font-mono">{user.id}</p>
                </div>
              </div>
            </div>

            <div className="section-card">
              <h3>Active Browser Sessions</h3>
              <div className="session-item">
                <div>
                  <strong>Current Session</strong>
                  <p className="session-meta">
                    Mac OS • Chrome 134 • IP: 106.195.42.12 (Chennai, IN)
                  </p>
                </div>
                <span className="badge badge-success">Active Now</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
